import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../utils/utils';

interface SidebarItemProps {
    to: string;
    text: string;
    icon: React.ReactNode;
    onClose: () => void;
    className?: string;
    /**
     * When true, the item is rendered visually muted, doesn't navigate,
     * and ignores hover styling. Used by the bookings entry while
     * self-service tenants are still working through the setup
     * checklist (see `useSetupProgress`).
     */
    disabled?: boolean;
    /** Optional tooltip shown when disabled. */
    disabledTitle?: string;
}

export const SidebarItem = ({
    className,
    text,
    icon,
    onClose,
    to,
    disabled = false,
    disabledTitle,
}: SidebarItemProps) => {
    const location = useLocation();
    const isActive = !disabled && location.pathname === to;

    if (disabled) {
        return (
            <div
                aria-disabled
                title={disabledTitle}
                className={cn(
                    'relative flex items-center w-full px-3 py-2.5 gap-3 rounded-lg',
                    'text-foreground/40 opacity-60 cursor-not-allowed select-none',
                    className,
                )}
            >
                <div>{icon}</div>
                <span className={'font-medium'}>{text}</span>
            </div>
        );
    }

    return (
        <Link
            to={to}
            onClick={onClose}
            className={cn(
                'relative flex items-center w-full px-3 py-2.5 transition-all duration-200 text-foreground/70',
                'hover:text-foreground hover:bg-foreground/5 gap-3 rounded-lg group',
                isActive && 'text-[var(--color-primary)] bg-gray-100 hover:bg-gray-200',
                className,
            )}
        >
            {isActive && (
                <div
                    className={'absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-all duration-200'}
                    style={{ backgroundColor: 'var(--color-primary)' }}
                />
            )}
            <div className={cn('transition-transform duration-200 group-hover:scale-110', isActive && 'scale-110')}>
                {icon}
            </div>
            <span className={cn('font-medium transition-colors duration-200', isActive && 'font-semibold')}>{text}</span>
        </Link>
    );
};
